<div class="shared-chart-page">
  @switch (state()) {
    @case ('invalid') {
      <div class="shared-chart-page__status shared-chart-page__status--invalid" data-test-id="shared-page-invalid">
        <i class="fas fa-link-slash shared-chart-page__status-icon" aria-hidden="true"></i>
        <p>{{ text().invalidLink }}</p>
        <button eBtn type="button" color="primary" icon="right-to-bracket" (click)="goToLogin()">{{ text().toLogin }}</button>
      </div>
    }
    @case ('busy') {
      <div class="shared-chart-page__status shared-chart-page__status--busy" data-test-id="shared-page-busy">
        <i class="fas fa-hourglass-half shared-chart-page__status-icon" aria-hidden="true"></i>
        <p>{{ text().tooManyRequests }}</p>
        <button eBtn type="button" color="primary" icon="rotate-right" (click)="retry()">{{ text().retry }}</button>
      </div>
    }
    @case ('ready') {
      @if (preset(); as preset) {
        <hd-shared-chart-banner
          mode="guest"
          [chartName]="preset.name"
          [sharedByName]="shared()?.meta?.sharedByName ?? null"
          [showRegister]="registrationEnabled()"
          (loginRequested)="goToLogin()"
          (registerRequested)="goToRegister()"
        >
          <!-- Taalkeuze (NL/EN) zoals op de loginpagina, naast de knoppen: de gast heeft geen account-voorkeur. -->
          <language-selector bannerExtra class="shared-chart-page__language" />
        </hd-shared-chart-banner>

        <!-- Shortcutbalk met de standaardsettings, volledig uitgeschakeld: een klik erop geeft de accountmelding. -->
        <div class="shared-chart-page__bar" data-test-id="shared-page-bar" (click)="accountRequired()">
          <hd-chart-shortcut-bar
            [mobile]="viewport.isPhone()"
            buttonSize="sm"
            [primaryGateAction]="defaults.chartGatePrimaryAction"
            [hasPlanetaryActivations]="hasPlanetaryActivations()"
            [showPlanetGrid]="defaults.showPlanetGrid"
            [simplePlanetGrid]="defaults.simplePlanetGrid"
            [advancedLpsGrid]="defaults.advancedLpsGrid"
            [planetGridVisualizationMode]="defaults.planetGridVisualizationMode"
            [includeNonHdFeatures]="defaults.includeNonHdFeatures"
            [showGeneKeys]="defaults.showGeneKeys"
            [showHdKeynotes]="defaults.showHdKeynotes"
            [showCenterCheatSheet]="defaults.showCenterCheatSheet"
            [showGateBlocks]="defaults.showGateBlocks"
            [cycleChartActive]="cycleInfo() != null"
            [compositeChartActive]="compositeInfo() != null"
            [showSideStats]="!viewport.isPhone()"
            viewMode="chart"
            sideMode="full"
          ></hd-chart-shortcut-bar>
        </div>

        <div class="shared-chart-page__content">
          <div class="shared-chart-page__chart-area" [class.shared-chart-page__chart-area--grids]="hasPlanetaryActivations()">
            @if (hasPlanetaryActivations() && renderState(); as renderState) {
              <div class="shared-chart-page__grid shared-chart-page__grid--design">
                @if (isCycleOrComposite()) {
                  <hd-just-now-base-planet-grid
                    [activations]="planetaryActivations()"
                    [transitActivations]="cycleMomentGridActivations()"
                    sideMode="full"
                    [lineData]="justNowTransitLineData"
                    [planetName]="justNowTransitPlanetName"
                    [labels]="justNowBasePlanetGridLabels()"
                    [chartLabel]="compositeChart1Label()"
                    [designChiron]="designChiron()"
                    [personalityChiron]="personalityChiron()"
                    [contextMenuEnabled]="false"
                    (planetActivationClicked)="accountRequired()"
                  />
                } @else {
                  <hd-planet-grid
                    [activations]="designActivations()"
                    side="design"
                    [renderState]="renderState"
                    [colorConfig]="defaults.planetGridColorConfig"
                    [visualizationMode]="defaults.planetGridVisualizationMode"
                    [showGeneKeys]="defaults.showGeneKeys"
                    [showHdKeynotes]="defaults.showHdKeynotes"
                    [showSideStats]="!viewport.isPhone()"
                    [oppositeActivations]="personalityActivations()"
                    [allActivations]="planetaryActivations()"
                    [statsOverallRenderState]="renderState"
                    [contextMenuEnabled]="false"
                    [chironDesign]="designChiron()"
                    [chironPersonality]="personalityChiron()"
                    (chironClicked)="accountRequired()"
                    (planetActivationClicked)="accountRequired()"
                    (phsBlockClicked)="accountRequired()"
                  />
                }
              </div>
            }

            <div class="shared-chart-page__chart">
              <hd-bodygraph-chart
                [configuration]="preset.configuration"
                [colorConfig]="defaults.colorConfig"
                [transitOverlay]="cycleOverlay()"
                [locksKeysOverlay]="locksKeysChartOverlay()"
                interactionMode="cycle"
                primaryGateAction="show-info"
                [contextMenuEnabled]="false"
                [channelHighlightEnabled]="defaults.channelHighlightEnabled"
                (gateClicked)="accountRequired()"
                (gateInfoRequested)="accountRequired()"
                (channelInfoRequested)="accountRequired()"
                (centerInfoRequested)="accountRequired()"
              ></hd-bodygraph-chart>
            </div>

            @if (hasPlanetaryActivations() && renderState(); as renderState) {
              <div class="shared-chart-page__grid shared-chart-page__grid--personality">
                @if (isCycleOrComposite()) {
                  <hd-just-now-base-planet-grid
                    [activations]="cycleMomentGridActivations()"
                    [transitActivations]="planetaryActivations()"
                    sideMode="full"
                    [lineData]="justNowTransitLineData"
                    [planetName]="justNowTransitPlanetName"
                    [labels]="justNowBasePlanetGridLabels()"
                    [titleAccentColor]="cycleTitleColor"
                    [chartLabel]="compositeChart2Label()"
                    [designChiron]="cycleMomentDesignChiron()"
                    [personalityChiron]="cycleMomentPersonalityChiron()"
                    [contextMenuEnabled]="false"
                    (planetActivationClicked)="accountRequired()"
                  />
                } @else {
                  <hd-planet-grid
                    [activations]="personalityActivations()"
                    side="personality"
                    [renderState]="renderState"
                    [colorConfig]="defaults.planetGridColorConfig"
                    [visualizationMode]="defaults.planetGridVisualizationMode"
                    [showGeneKeys]="defaults.showGeneKeys"
                    [showHdKeynotes]="defaults.showHdKeynotes"
                    [showSideStats]="!viewport.isPhone()"
                    [oppositeActivations]="designActivations()"
                    [allActivations]="planetaryActivations()"
                    [statsOverallRenderState]="renderState"
                    [contextMenuEnabled]="false"
                    (planetActivationClicked)="accountRequired()"
                    (phsBlockClicked)="accountRequired()"
                  />
                }
              </div>
            }
          </div>

          <!-- Rechterpaneel: dezelfde dock-tabs als de chartomgeving (chip-strip blijft plakken bij scrollen). -->
          <aside class="shared-chart-page__panels">
            <dock-area #dock [zones]="['small', 'full']" [sideBySideFixedTabs]="false">
              <ng-template [dockFixedTab]="text().tabBasic" dockFixedTabId="basic">
                <hd-basic-properties
                  [configuration]="preset.configuration"
                  [planetaryActivations]="planetaryActivations()"
                  [advancedBridgeCalculation]="defaults.advancedBridgeCalculation"
                  [bridgeGateStyle]="defaults.bridgeGateStyle"
                  [environmentMatchesEnabled]="false"
                  [matchScope]="defaults.chartMatchScope"
                  [savedChartId]="null"
                  [chironPositions]="[]"
                  [chartLinked]="false"
                  viewMode="guest"
                  (accountRequired)="accountRequired()"
                  (gateInfoRequested)="accountRequired()"
                  (channelInfoRequested)="accountRequired()"
                  (chartOpenRequested)="accountRequired()"
                  (lifeCrossInfoRequested)="accountRequired()"
                  (profileInfoRequested)="accountRequired()"
                  (incarnationCrossInfoRequested)="accountRequired()"
                  (basicsInfoRequested)="accountRequired()"
                  (typeInfoRequested)="accountRequired()"
                  (authorityInfoRequested)="accountRequired()"
                  (definitionInfoRequested)="accountRequired()"
                  (conditioningInfoRequested)="accountRequired()"
                  (centerInfoRequested)="accountRequired()"
                  (chironClicked)="accountRequired()"
                ></hd-basic-properties>
              </ng-template>
              <ng-template [dockFixedTab]="text().tabAdvanced" dockFixedTabId="advanced">
                <hd-advanced-properties
                  [birthData]="null"
                  [savedChartId]="null"
                  [chartName]="preset.name"
                  [chartType]="chartType()"
                  [preset]="preset"
                  [matchScope]="defaults.chartMatchScope"
                  [bridgeGateStyle]="defaults.bridgeGateStyle"
                  viewMode="guest"
                  (accountRequired)="accountRequired()"
                  (chartOpenRequested)="accountRequired()"
                  (chartOpenWithNotesRequested)="accountRequired()"
                  (timelineRequested)="accountRequired()"
                  (cycleCalculateRequested)="accountRequired()"
                  (cycleJustNowTransitRequested)="accountRequired()"
                  (cycleRecalculateRequested)="accountRequired()"
                  (cycleLifeCrossInfoRequested)="accountRequired()"
                  (cycleDefinitionInfoRequested)="accountRequired()"
                  (compositeCalculateRequested)="accountRequired()"
                  (compositeThemeInfoRequested)="accountRequired()"
                  (relationInfoRequested)="accountRequired()"
                  (gateInfoRequested)="accountRequired()"
                  (channelInfoRequested)="accountRequired()"
                  (centerInfoRequested)="accountRequired()"
                  (locksKeysRequested)="accountRequired()"
                  (birthWindowRequested)="accountRequired()"
                  (conditioningInfoRequested)="accountRequired()"
                ></hd-advanced-properties>
              </ng-template>
            </dock-area>
          </aside>
        </div>
      }
    }
    @default {
      <div class="shared-chart-page__status" data-test-id="shared-page-loading">{{ text().loading }}</div>
    }
  }
</div>
